import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/Admin/Sidebar';
import AdminBottomNavBar from '../../components/Admin/AdminBottomNavBar';
import useAttendanceStore from '../../stores/attendanceStore';
import useStudentsStore from '../../stores/studentsStore';
import useGroupsStore from '../../stores/groupsStore';
import { formatDateToRussianWithYear } from '../../utils/dateUtils';

function AdminAttendanceSlot() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const scheduleId = params.get('scheduleId');
  const slotKeyFromUrl = params.get('slotKey');
  const slotIndexFromUrl = params.get('slotIndex');

  const { fetchGroupAttendance, markAttendance, groupAttendance, loading, fetchScheduleContext, scheduleContext, fetchScheduleAttendance, scheduleAttendance } = useAttendanceStore();
  const [localStatus, setLocalStatus] = useState({});
  const { students, fetchStudents } = useStudentsStore();
  const { groups, fetchGroups } = useGroupsStore();

  useEffect(() => { if (scheduleId) fetchScheduleContext(scheduleId); }, [scheduleId, fetchScheduleContext]);
  const groupId = scheduleContext?.group?._id || scheduleContext?.group || '';
  const date = scheduleContext?.date || '';
  const resolveSlot = () => {
    const slots = scheduleContext?.timeSlots || [];
    if (!slots.length) return null;
    if (slotIndexFromUrl && !Number.isNaN(Number(slotIndexFromUrl))) return slots[Number(slotIndexFromUrl)] || slots[0];
    if (slotKeyFromUrl) {
      const found = slots.find(s => `t_${s.startTime}_${s.endTime || ''}` === slotKeyFromUrl);
      if (found) return found;
    }
    return slots[0];
  };
  const currentSlot = resolveSlot();
  const timeLabel = currentSlot ? `${currentSlot.startTime}${currentSlot.endTime ? `–${currentSlot.endTime}` : ''}` : '';
  const group = useMemo(() => groups.find(g => String(g._id) === String(groupId)), [groups, groupId]);
  const groupStudents = useMemo(() => {
    if (scheduleContext?.students?.length) return scheduleContext.students;
    // Если в расписании нет явного списка студентов, возьмём из отметок — это даёт хотя бы тех, кому уже ставили статус
    if (scheduleAttendance?.length) {
      const map = new Map(scheduleAttendance.map(a => [String(a.student?._id || a.student), a.student]));
      const list = Array.from(map.values()).filter(Boolean).map(s => ({ _id: s._id, fullName: s.fullName }));
      if (list.length) return list;
    }
    return students.filter(s => (Array.isArray(s.groups) ? s.groups : [s.group]).some(g => String(g?._id || g) === String(groupId)));
  }, [students, groupId, scheduleContext, scheduleAttendance]);

  useEffect(() => { fetchGroups(); fetchStudents(); }, [fetchGroups, fetchStudents]);

  useEffect(() => {
    if (!groupId || !date) return;
    fetchGroupAttendance({ groupId, startDate: date, endDate: date });
  }, [groupId, date, fetchGroupAttendance]);

  // Загружаем текущие отметки именно для этого занятия и слота
  useEffect(() => {
    if (!scheduleId) return;
    const startTime = currentSlot?.startTime;
    const endTime = currentSlot?.endTime;
    const slotKey = startTime ? `t_${startTime}_${endTime || ''}` : '';
    fetchScheduleAttendance({ scheduleId, slotKey });
  }, [scheduleId, currentSlot, fetchScheduleAttendance]);

  // Синхронизируем локальные значения с серверными после загрузки
  useEffect(() => {
    const map = {};
    (scheduleAttendance || []).forEach(a => {
      const sid = String(a.student?._id || a.student);
      map[sid] = a.status || '';
    });
    if (Object.keys(map).length) setLocalStatus(prev => ({ ...prev, ...map }));
  }, [scheduleAttendance]);

  const mapStatus = useMemo(() => {
    const m = new Map();
    (scheduleAttendance || []).forEach(a => {
      m.set(String(a.student?._id || a.student), a);
    });
    return m;
  }, [scheduleAttendance]);

  const setStatus = async (sid, status) => {
    const startTime = currentSlot?.startTime;
    const endTime = currentSlot?.endTime;
    const subjectId = currentSlot?.subject?._id || currentSlot?.subject;
    const slotKey = startTime ? `t_${startTime}_${endTime || ''}` : '';
    // Оптимистичное обновление
    setLocalStatus(prev => ({ ...prev, [String(sid)]: status }));
    try {
      await markAttendance({ students: [sid], groupId, date, startTime, endTime, subjectId, status, scheduleId, slotKey });
      await fetchScheduleAttendance({ scheduleId, slotKey });
    } catch (e) {
      alert(e.message || 'Не удалось сохранить отметку');
    }
  };

  return (
    <div className='AdminAttendanceSlot'>
      <div className="AdminAttendanceSlot__container">
        <Sidebar className="admin-sidebar" />
        <div className="content_admin" style={{ marginLeft: 240 }}>
          <button className="admin-user-back-btn" onClick={() => navigate(-1)}>Назад</button>
          <h2 style={{ margin: '12px 0 18px' }}>
            Посещаемость занятия: {date ? (() => {
              const originalDate = new Date(date);
              originalDate.setDate(originalDate.getDate() + 1);
              return formatDateToRussianWithYear(originalDate.toISOString().slice(0, 10));
            })() : ''} {timeLabel} {group ? `— ${group.name}` : ''}
          </h2>

          <div style={{ overflowX: 'auto' }}>
            <table className="attendance-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 8 }}>Ученик</th>
                  <th style={{ textAlign: 'left', padding: 8, width: 220 }}>Статус</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {groupStudents.map(s => {
                  const sid = String(s._id);
                  const rec = mapStatus.get(sid);
                  const value = localStatus[sid] ?? (rec?.status || '');
                  return (
                    <tr key={s._id}>
                      <td style={{ padding: 8 }}>{s.fullName}</td>
                      <td style={{ padding: 8 }}>
                        <select value={value} onChange={e => setStatus(s._id, e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #E0E0E6' }}>
                          <option value=''>—</option>
                          <option value='present'>Был</option>
                          <option value='absent'>Не был</option>
                        </select>
                      </td>
                      <td />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <AdminBottomNavBar />
    </div>
  );
}

export default AdminAttendanceSlot;


